/**
 * Which TIA project a Siemens validation compiles in, on which Agent, and
 * whether that Agent can do it now (TIA 根治 P2).
 *
 * The single definition point for:
 * - a project reference and the registry entries an Agent reports;
 * - the TIA facts the gateway builds from one Agent's heartbeats;
 * - the target resolution that web, gateway and runtime all call;
 * - readiness, its wording, and its twin in the older eligibility union.
 *
 * Pure data and pure functions: nothing here reads a clock or the network.
 */
import { TIA_VALIDATION_CODES, } from './tiaValidation.js';
const TIA_PROJECT_SOURCES = { open: true, recent: true, scanned: true };
function isTiaProjectSource(value) {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(TIA_PROJECT_SOURCES, value);
}
/**
 * The project reference `value` holds, or undefined when it holds none.
 *
 * A reference comes from outside on every hop between the picker and the
 * validation run: a request, the stored session snapshot, a job's context, a
 * workflow checkpoint. Each reader takes it through here, so a malformed one
 * reads as no reference instead of reaching resolveTiaTarget. The copy it
 * returns holds the reference's own fields and nothing else.
 */
export function readTiaProjectRef(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    const ref = value;
    if (ref.kind === 'new')
        return { kind: 'new' };
    if (ref.kind !== 'agent')
        return undefined;
    const { agentId, id, displayName, source } = ref;
    if (typeof agentId !== 'string' || !agentId || typeof id !== 'string' || !id)
        return undefined;
    if (typeof displayName !== 'string' || !isTiaProjectSource(source))
        return undefined;
    return { kind: 'agent', agentId, id, displayName, source };
}
/** Whether two references name the same project, and name it the same way. */
export function sameTiaProjectRef(a, b) {
    if (!a || !b)
        return a === b;
    if (a.kind === 'new' || b.kind === 'new')
        return a.kind === b.kind;
    return a.agentId === b.agentId && a.id === b.id && a.displayName === b.displayName && a.source === b.source;
}
/**
 * Whether two references name the same project, whatever names they carry:
 * the kind, and for an entry its Agent and that Agent's local ID; no
 * reference is the same as no reference.
 *
 * What the web confirms a stored choice by (T5 review): a server that stored
 * the reference with another display name or source would otherwise leave
 * the choice unsent for good, and the page would send a stale reference with
 * every turn. sameTiaProjectRef stays for where the names matter too.
 */
export function sameTiaProjectIdentity(a, b) {
    if (!a || !b)
        return a === b;
    if (a.kind === 'new' || b.kind === 'new')
        return a.kind === b.kind;
    return a.agentId === b.agentId && a.id === b.id;
}
/**
 * The server-written fields (SERVER_SESSION_CONFIG_FIELDS) of a snapshot, or
 * of anything that carries them from outside: a stored snapshot, a turn's
 * accepted chunk, a `tia_target_resolved` chunk. The resolved CPU model comes
 * only with a known source, as a pair; the reference only well formed.
 * Anything else is left out, so what this returns is the whole of what the
 * value says about them: a field it lacks, the value holds none of.
 */
export function readServerSessionConfig(value) {
    const record = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    const fields = {};
    const plcModel = typeof record.plcModel === 'string' ? record.plcModel.trim() : '';
    if (plcModel && isPlcModelSource(record.plcModelSource)) {
        fields.plcModel = plcModel;
        fields.plcModelSource = record.plcModelSource;
    }
    const tiaProjectRef = readTiaProjectRef(record.tiaProjectRef);
    if (tiaProjectRef)
        fields.tiaProjectRef = tiaProjectRef;
    return fields;
}
/**
 * Whether a project is open in the user's TIA: itself, or its `_RPV_`
 * validation copy, which counts as the project open (E7). The one reading of
 * it, for the target resolution's default and for the picker's open group.
 */
export function isTiaEntryOpen(entry) {
    return entry.open || entry.openAsValidationCopy === true;
}
/**
 * Whether an Agent keeps a project registry, which is what P2 calls a new
 * Agent. Only such an Agent can create a project in a validation job or
 * resolve a registry entry.
 *
 * The registry itself is the mark: the Agent release that reports `projects`
 * is the one that takes a target in the validation job (T6 and T7 ship
 * together). Declared actions are not consulted. They switch the picker's
 * buttons one by one (T8, T11), and an action list filtered by readiness on
 * its way through the gateway would make a new Agent look old exactly when
 * TIA is not running, the one case where the two behave differently.
 */
export function hasTiaProjectRegistry(facts) {
    return Array.isArray(facts.projects);
}
/**
 * The one target a validation compiles in: which Agent, and which project on
 * it. Web, gateway and runtime all call this, and E2's precedence and E7's
 * defaults are written nowhere else. It replaces the gateway's
 * `pickBestOnlineAgent` and `getByProject` (T3, T4, T8).
 *
 * The ref is the session ref when there is one, taken whole; else the
 * project default.
 *
 * The Agent, by the first rule that applies:
 * 1. A ref to a registry entry names its Agent. Absent from `agents` →
 *    TIA_PROJECT_REF_NOT_FOUND; offline → none: wait for it, never another.
 * 2. `agentSelector` in mode `specific` names an Agent; absent or offline →
 *    none. A ref to a new project leaves the Agent to this rule and the
 *    next, which is what lets 「新建工程」 escape a default whose Agent is gone.
 * 3. Otherwise automatically, among the workspace's online Agents:
 *    the one closest to serving (deriveTiaReadiness on each one's own
 *    target): ready, then busy, then still checking, then waiting on a choice
 *    or an upgrade, then a credential, then a capability, then the
 *    environment. Among equals the lower Agent ID wins. The order is stable
 *    on purpose: heartbeats of two Agents land milliseconds apart, so "the
 *    most recent" would flip between them on every poll, and being online
 *    already covers liveness. With no online Agent, resolve to none.
 *
 * Pairing connects the workspace to a workstation. Callers supply only that
 * workspace's Agents and authorize access to each RealPLC project separately.
 * Legacy pairing project IDs do not restrict selection or execution.
 *
 * The project, on that Agent:
 * - Without a registry (hasTiaProjectRegistry): any ref →
 *   AGENT_UPGRADE_REQUIRED, since such an Agent can neither create a project
 *   nor look up an entry. No ref → legacy_open when it reports a project
 *   open; else TIA_NOT_RUNNING when it says TIA is not running; else
 *   AGENT_UPGRADE_REQUIRED, whose words also cover starting TIA.
 * - With a registry: a ref to a new project → new; a ref to an entry → that
 *   entry, or TIA_PROJECT_REF_NOT_FOUND. No ref → the one open project (an
 *   open validation copy counts as its project open), several →
 *   TIA_PROJECT_NOT_SELECTED, none → new. TIA not running never blocks such
 *   an Agent: it starts its own Portal.
 *
 * Before an Agent's first probe its open projects are unknown, so what this
 * returns for it is provisional. deriveTiaReadiness then says `checking`; a
 * caller pins or dispatches only a target whose readiness is `ready`.
 */
export function resolveTiaTarget(input) {
    const ref = input.sessionRef ?? input.projectDefaultRef;
    const find = (agentId) => input.agents.find((agent) => agent.agentId === agentId);
    if (ref?.kind === 'agent') {
        const agent = find(ref.agentId);
        if (!agent)
            return { kind: 'blocked', code: TIA_VALIDATION_CODES.TIA_PROJECT_REF_NOT_FOUND };
        return targetOnNamedAgent(agent, ref);
    }
    const selected = input.agentSelector?.mode === 'specific' ? input.agentSelector.agentId : undefined;
    if (selected) {
        const agent = find(selected);
        return agent ? targetOnNamedAgent(agent, ref) : { kind: 'none' };
    }
    return targetOnBestAgent(input.agents, ref);
}
function blocked(agentId, code) {
    return { kind: 'blocked', agentId, code };
}
function targetOnNamedAgent(agent, ref) {
    if (!agent.online)
        return { kind: 'none' };
    return targetOn(agent, ref);
}
function targetOnBestAgent(agents, ref) {
    const [best] = agents
        .filter((agent) => agent.online)
        .map((agent) => {
        const target = targetOn(agent, ref);
        return { agent, target, rank: CLOSEST_TO_SERVING[deriveTiaReadiness(agent, target, undefined).state] };
    })
        .sort((a, b) => a.rank - b.rank || byAgentId(a.agent, b.agent));
    return best?.target ?? { kind: 'none' };
}
const AWAITING_SOMEONE = [
    'no_project_selected',
    'project_not_found',
    'upgrade_required',
    'tia_not_running',
    'project_mismatch',
];
/**
 * The states in which the target waits on someone: to choose a project, open
 * one or switch back to it, start TIA, or upgrade the Agent. One group of the
 * automatic choice's ranking (CLOSEST_TO_SERVING), and what the project
 * picker says it is waiting for.
 */
export const TIA_READINESS_AWAITING_SOMEONE = new Set(AWAITING_SOMEONE);
/**
 * How close an Agent is to serving, best first, as the gateway's old
 * automatic choice ranked it (api-gateway `agentRegistry.ts`
 * READINESS_PREFERENCE) in TiaReadiness terms. States that share a number
 * rank alike, and the Agent ID decides between them.
 */
const CLOSEST_TO_SERVING = {
    ready: 0,
    busy: 1,
    checking: 2,
    ...Object.fromEntries(AWAITING_SOMEONE.map((state) => [state, 3])),
    auth_required: 4,
    capability_missing: 5,
    version_incompatible: 5,
    environment_not_ready: 6,
    // Never among the candidates, which are online workspace Agents.
    not_authorized: 7,
    offline: 7,
};
function byAgentId(a, b) {
    if (a.agentId === b.agentId)
        return 0;
    return a.agentId < b.agentId ? -1 : 1;
}
/** The project on one Agent: E1 for an Agent without a registry, E2 and E7 for one with it. */
function targetOn(agent, ref) {
    const { agentId } = agent;
    if (!hasTiaProjectRegistry(agent)) {
        if (ref)
            return blocked(agentId, TIA_VALIDATION_CODES.AGENT_UPGRADE_REQUIRED);
        if (agent.openProject) {
            const { displayName, fingerprint } = agent.openProject;
            return { kind: 'legacy_open', agentId, displayName, fingerprint };
        }
        return blocked(agentId, agent.tiaRunning === false ? TIA_VALIDATION_CODES.TIA_NOT_RUNNING : TIA_VALIDATION_CODES.AGENT_UPGRADE_REQUIRED);
    }
    if (ref?.kind === 'new')
        return { kind: 'new', agentId };
    if (ref?.kind === 'agent') {
        const named = agent.projects.find((candidate) => candidate.id === ref.id);
        return named ? { kind: 'entry', agentId, entry: named } : blocked(agentId, TIA_VALIDATION_CODES.TIA_PROJECT_REF_NOT_FOUND);
    }
    const open = agent.projects.filter(isTiaEntryOpen);
    if (open.length > 1)
        return blocked(agentId, TIA_VALIDATION_CODES.TIA_PROJECT_NOT_SELECTED);
    const [only] = open;
    return only ? { kind: 'entry', agentId, entry: only } : { kind: 'new', agentId };
}
// ---------------------------------------------------------------------------
// Readiness
// ---------------------------------------------------------------------------
/**
 * Every state the TIA button, the gate and the settings dialog report.
 *
 * It replaces three older unions — `AgentReadinessState` and `AgentReadyState`
 * (api-gateway `opennessTypes.ts`), `AgentEligibilityState` (tiaValidation.ts)
 * and the browser's copy in `services/opennessAgent.ts` — and each state they
 * produce has its place here:
 * - ready → ready; busy, agent_busy → busy;
 * - auth_required, agent_auth_required, credential_invalid → auth_required;
 * - environment_not_ready, tia_not_installed, public_api_not_found,
 *   openness_permission_missing, worker_missing, diagnostics_failed →
 *   environment_not_ready;
 * - version_incompatible → version_incompatible;
 *   capability_missing → capability_missing;
 * - project_mismatch for a pairing bound elsewhere → not_authorized, and for
 *   a fingerprint that no longer matches → project_mismatch;
 * - tia_session_required, tia_not_running → tia_not_running, for an old
 *   Agent only: a new Agent starts its own Portal;
 * - project_not_open → checking before the first probe; after it,
 *   upgrade_required on an old Agent, or a `new` target on a new one;
 * - offline (no live Agent) → offline.
 * no_project_selected and project_not_found are new with project references.
 */
export const TIA_READINESS_STATES = [
    'offline',
    'checking',
    'busy',
    'auth_required',
    'environment_not_ready',
    'version_incompatible',
    'capability_missing',
    'not_authorized',
    'tia_not_running',
    'no_project_selected',
    'project_not_found',
    'project_mismatch',
    'upgrade_required',
    'ready',
];
/** The action a TIA connector must declare to validate SCL at all. */
const VALIDATE_SCL_ACTION = 'tia_validate_scl';
const BLOCK_STATE = {
    TIA_NOT_RUNNING: 'tia_not_running',
    TIA_PROJECT_NOT_SELECTED: 'no_project_selected',
    TIA_PROJECT_REF_NOT_FOUND: 'project_not_found',
    AGENT_UPGRADE_REQUIRED: 'upgrade_required',
    AGENT_TARGET_NOT_AUTHORIZED: 'not_authorized',
};
function fromBlock(code) {
    return { state: BLOCK_STATE[code], code };
}
/**
 * Whether the target's Agent can compile it now, and if not, the first thing
 * in the way (门禁只显示第一个阻塞原因).
 *
 * `facts` are the target Agent's. `resolved` is resolveTiaTarget's answer, or
 * a target a run pinned earlier (E11 ②), which is checked against the Agent
 * as it is now. The third argument retains the RealPLC project context for
 * existing callers; project authorization belongs to the gateway, separately
 * from the workspace's connection to its workstation.
 * First match wins:
 *
 * 1. offline: no usable Agent, or its facts say so. A ref naming an Agent that
 *    is gone reads project_not_found instead: choosing again is the way out.
 * 2. auth_required: the connection's credentials need to be renewed.
 * 3. not_authorized: an explicit blocker from an earlier gateway only.
 * 4. environment_not_ready, then capability_missing: no `tia_validate_scl`,
 *    or no validation in an isolated copy. Both before checking, because an
 *    Agent that cannot run Openness never reports a probe.
 * 5. checking: never ready before the first probe.
 * 6. the resolver's block: tia_not_running (old Agents only),
 *    upgrade_required, no_project_selected, project_not_found.
 * 7. a pinned target that no longer holds: project_mismatch when an old
 *    Agent's open project changed, project_not_found when the entry left the
 *    registry, upgrade_required when a `new` target's Agent has no registry.
 * 8. busy: after the target, so what the user must choose is named while
 *    the running job finishes on its own.
 * 9. ready.
 *
 * version_incompatible is never derived here, since no fact states a required
 * version: it is for a caller's version requirement and for the Worker's
 * TIA_PROJECT_VERSION_UNSUPPORTED.
 */
export function deriveTiaReadiness(facts, resolved, _projectId) {
    if (resolved.kind === 'none')
        return { state: 'offline' };
    if (!facts) {
        return resolved.kind === 'blocked' && resolved.code === TIA_VALIDATION_CODES.TIA_PROJECT_REF_NOT_FOUND
            ? fromBlock(resolved.code)
            : { state: 'offline' };
    }
    if (!facts.online)
        return { state: 'offline' };
    if (facts.authRequired)
        return { state: 'auth_required' };
    // Keep understanding explicit blockers from older gateways without deriving
    // a project restriction from legacy pairing metadata.
    if (resolved.kind === 'blocked' && resolved.code === TIA_VALIDATION_CODES.AGENT_TARGET_NOT_AUTHORIZED) {
        return fromBlock(TIA_VALIDATION_CODES.AGENT_TARGET_NOT_AUTHORIZED);
    }
    if (!facts.environmentReady)
        return { state: 'environment_not_ready' };
    if (!facts.supportedActions.includes(VALIDATE_SCL_ACTION) || !facts.supportsValidateSandbox) {
        return { state: 'capability_missing' };
    }
    if (!facts.probed)
        return { state: 'checking' };
    if (resolved.kind === 'blocked')
        return fromBlock(resolved.code);
    const drift = pinnedTargetDrift(facts, resolved);
    if (drift)
        return drift;
    if (facts.busy)
        return { state: 'busy' };
    return { state: 'ready' };
}
/** How a target no longer matches its Agent's current facts, if it does not. */
function pinnedTargetDrift(facts, resolved) {
    switch (resolved.kind) {
        case 'legacy_open':
            return facts.openProject?.fingerprint === resolved.fingerprint ? undefined : { state: 'project_mismatch' };
        case 'entry':
            return facts.projects?.some((candidate) => candidate.id === resolved.entry.id)
                ? undefined
                : fromBlock(TIA_VALIDATION_CODES.TIA_PROJECT_REF_NOT_FOUND);
        case 'new':
            return hasTiaProjectRegistry(facts) ? undefined : fromBlock(TIA_VALIDATION_CODES.AGENT_UPGRADE_REQUIRED);
    }
}
/**
 * Each readiness state as the older eligibility union says it, for the
 * gateway's `AgentEligibilityResult` until the runtime reads TiaReadiness
 * itself (T3, T9). A state without an exact twin takes the one today's code
 * already gives the same situation, so nothing the runtime keys on moves.
 */
export const TIA_READINESS_ELIGIBILITY_STATE = {
    offline: 'offline',
    // No twin. evaluateAgent answers project_not_open for an Agent that has not
    // probed yet, having no fingerprint, and the runtime's words for that state
    // describe this very wait.
    checking: 'project_not_open',
    busy: 'busy',
    auth_required: 'auth_required',
    environment_not_ready: 'environment_not_ready',
    version_incompatible: 'version_incompatible',
    capability_missing: 'capability_missing',
    // No twin. evaluateAgent reports a pairing bound to another project as
    // project_mismatch, with the blocker code AGENT_TARGET_NOT_AUTHORIZED.
    not_authorized: 'project_mismatch',
    // No twin. evaluateAgent maps the Agent's readyState tia_not_running here.
    tia_not_running: 'environment_not_ready',
    // No twin: someone picks the project, then the run resumes.
    no_project_selected: 'project_not_open',
    // No twin: someone picks the project again, then the run resumes.
    project_not_found: 'project_not_open',
    project_mismatch: 'project_mismatch',
    // No twin. An old Agent with nothing open reads project_not_open today, and
    // opening a project still clears it (E1 ②); upgrading is the other way out.
    upgrade_required: 'project_not_open',
    ready: 'ready',
};
/**
 * The words for each state, written only here. `label` is the TIA button's
 * text; `explanation` is one sentence on what is going on and what to do.
 */
export const TIA_READINESS_TEXT = {
    offline: {
        label: { zh: '离线', en: 'Offline' },
        explanation: {
            zh: 'RealPLC Agent 没有连接，程序可以下载，但不能在 TIA 里编译验证。',
            en: 'The RealPLC Agent is not connected, so the program can be downloaded but not compiled in TIA.',
        },
    },
    checking: {
        label: { zh: '在线 · 检测中', en: 'Online · Checking' },
        explanation: {
            zh: 'Agent 已连接，正在检测 TIA 和打开的工程，通常一分钟内完成。',
            en: 'The Agent is connected and still checking TIA and its open projects, which usually takes under a minute.',
        },
    },
    busy: {
        label: { zh: '在线 · 忙碌中', en: 'Online · Busy' },
        explanation: {
            zh: 'Agent 正忙，验证会排队。',
            en: 'The Agent is busy; the validation will queue behind its current job.',
        },
    },
    auth_required: {
        label: { zh: '在线 · 需重新配对', en: 'Online · Pair again' },
        explanation: {
            zh: 'Agent 的配对凭据已失效，请在 Agent 设置里重新配对。',
            en: "The Agent's pairing credential is no longer valid; pair it again in the Agent settings.",
        },
    },
    environment_not_ready: {
        label: { zh: '在线 · 环境未就绪', en: 'Online · Environment not ready' },
        explanation: {
            zh: 'Agent 所在电脑上的 TIA Portal 或 Openness 还没准备好，请按 Agent 界面的提示检查。',
            en: "TIA Portal or Openness on the Agent's computer is not ready; the Agent window says what is missing.",
        },
    },
    version_incompatible: {
        label: { zh: '在线 · TIA 版本不符', en: 'Online · TIA version mismatch' },
        explanation: {
            zh: '这次验证需要的 TIA Portal 版本与 Agent 使用的版本不一致。',
            en: 'This validation needs a different TIA Portal version from the one the Agent uses.',
        },
    },
    capability_missing: {
        label: { zh: '在线 · Agent 不支持验证', en: 'Online · Agent cannot validate' },
        explanation: {
            zh: '这个版本的 Agent 不能在隔离副本里编译 SCL，请升级 Agent。',
            en: 'This Agent version cannot compile SCL in an isolated copy; upgrade the Agent.',
        },
    },
    not_authorized: {
        label: { zh: '在线 · 未授权此项目', en: 'Online · Not authorized for this project' },
        explanation: {
            zh: '这台 Agent 没有授权给当前的 RealPLC 项目，请从这个项目重新配对或共享它。',
            en: 'This Agent is not authorized for the current RealPLC project; pair or share it from this project.',
        },
    },
    tia_not_running: {
        label: { zh: '在线 · TIA 未启动', en: 'Online · TIA not running' },
        explanation: {
            zh: 'Agent 所在电脑上的 TIA Portal 没有运行：请启动 TIA 并打开一个工程，或升级 Agent，升级后不启动 TIA 也能验证。',
            en: "TIA Portal is not running on the Agent's computer: start it and open a project, or upgrade the Agent, which then validates without it.",
        },
    },
    no_project_selected: {
        label: { zh: '在线 · 未选工程', en: 'Online · No project selected' },
        explanation: {
            zh: 'TIA 里开着好几个工程，请在输入框上方选一个。',
            en: 'Several projects are open in TIA; choose one above the input box.',
        },
    },
    // No 「在线」: the ref may name an Agent that is gone altogether.
    project_not_found: {
        label: { zh: '找不到所选工程', en: 'Selected project not found' },
        explanation: {
            zh: '所选工程已不在它的 Agent 上（换了电脑、重新配对，或工程被移动、删除），请重新选择。',
            en: 'The selected project is no longer on its Agent (another computer, a new pairing, or a moved or deleted project); choose again.',
        },
    },
    project_mismatch: {
        label: { zh: '在线 · 打开的工程不符', en: 'Online · Another project is open' },
        explanation: {
            zh: 'TIA 里现在打开的不是这次要验证的工程，请切回原工程。',
            en: 'The project open in TIA is no longer the one this validation targets; switch back to it.',
        },
    },
    upgrade_required: {
        label: { zh: '在线 · 需升级 Agent', en: 'Online · Upgrade the Agent' },
        explanation: {
            zh: '在 TIA 里打开一个工程，或升级 Agent 后可自动新建。',
            en: 'Open a project in TIA, or upgrade the Agent so it can create one automatically.',
        },
    },
    ready: {
        label: { zh: '在线 · 工程就绪', en: 'Online · Project ready' },
        explanation: {
            zh: 'Agent 与 TIA 已就绪，可以编译验证这个程序。',
            en: 'The Agent and TIA are ready to compile this program.',
        },
    },
};
const PLC_MODEL_SOURCES = {
    project: true,
    requirement: true,
    settings: true,
    default: true,
};
/** Whether `value` is one of the sources a session's model can have. */
export function isPlcModelSource(value) {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(PLC_MODEL_SOURCES, value);
}
// The three family representatives, rows of TIA_CPUS: what a family name, or
// a model the table does not hold, is compiled for.
const CPU_1214C = { family: 'S7-1200', model: 'CPU 1214C DC/DC/DC', orderNumber: '6ES7 214-1AG40-0XB0' };
const CPU_1511_1_PN = {
    family: 'S7-1500', model: 'CPU 1511-1 PN', orderNumber: '6ES7 511-1AL03-0AB0', orderNumberBeforeV18: '6ES7 511-1AK02-0AB0',
};
const CPU_1512SP_1_PN = {
    family: 'ET 200SP', model: 'CPU 1512SP-1 PN', orderNumber: '6ES7 512-1DM03-0AB0', orderNumberBeforeV18: '6ES7 512-1DK01-0AB0',
};
/**
 * The CPUs a program is compiled for by name, model → article number →
 * family: the common current models of each family (「需求写了型号就直接用」).
 *
 * Every article number was read off its Siemens data sheet, fetched on
 * 2026-09-24 from the data sheet service SiePortal links
 * (support.industry.siemens.com/teddatasheet/?mlfbs=<article>). Each sheet
 * names the row's `model` as its product type designation and states the TIA
 * Portal versions noted below. A number that cannot be checked there does not
 * go in.
 */
export const TIA_CPUS = [
    // S7-1200: firmware V4.7, for which the sheets say "STEP 7 V20 or higher".
    // They name no other article for older TIA versions.
    { family: 'S7-1200', model: 'CPU 1211C DC/DC/DC', orderNumber: '6ES7 211-1AE40-0XB0' },
    { family: 'S7-1200', model: 'CPU 1212C DC/DC/DC', orderNumber: '6ES7 212-1AE40-0XB0' },
    CPU_1214C,
    { family: 'S7-1200', model: 'CPU 1215C DC/DC/DC', orderNumber: '6ES7 215-1AG40-0XB0' },
    { family: 'S7-1200', model: 'CPU 1217C DC/DC/DC', orderNumber: '6ES7 217-1AG40-0XB0' },
    { family: 'S7-1200', model: 'CPU 1214FC DC/DC/DC', orderNumber: '6ES7 214-1AF40-0XB0' },
    // S7-1500, the 03 generation: TIA Portal V18 (FW V3.0) or higher, each sheet
    // naming the article older TIA versions configure it as.
    CPU_1511_1_PN,
    { family: 'S7-1500', model: 'CPU 1513-1 PN', orderNumber: '6ES7 513-1AM03-0AB0', orderNumberBeforeV18: '6ES7 513-1AL02-0AB0' },
    { family: 'S7-1500', model: 'CPU 1515-2 PN', orderNumber: '6ES7 515-2AN03-0AB0', orderNumberBeforeV18: '6ES7 515-2AM02-0AB0' },
    { family: 'S7-1500', model: 'CPU 1516-3 PN/DP', orderNumber: '6ES7 516-3AP03-0AB0', orderNumberBeforeV18: '6ES7 516-3AN02-0AB0' },
    // One article across TIA versions, with no 03 generation: from TIA Portal V13
    // Update 3 (FW V1.6), firmware V3.1 from V19.
    { family: 'S7-1500', model: 'CPU 1517-3 PN/DP', orderNumber: '6ES7 517-3AP00-0AB0' },
    // One article across TIA versions, with no 03 generation: from TIA Portal V13
    // (FW V1.5), firmware V2.9 from V17.
    { family: 'S7-1500', model: 'CPU 1518-4 PN/DP', orderNumber: '6ES7 518-4AP00-0AB0' },
    { family: 'S7-1500', model: 'CPU 1511F-1 PN', orderNumber: '6ES7 511-1FL03-0AB0', orderNumberBeforeV18: '6ES7 511-1FK02-0AB0' },
    // ET 200SP: TIA Portal V18 (FW V3.0) or higher, each sheet naming the article
    // older TIA versions configure it as.
    { family: 'ET 200SP', model: 'CPU 1510SP-1 PN', orderNumber: '6ES7 510-1DK03-0AB0', orderNumberBeforeV18: '6ES7 510-1DJ01-0AB0' },
    CPU_1512SP_1_PN,
    { family: 'ET 200SP', model: 'CPU 1512SP F-1 PN', orderNumber: '6ES7 512-1SM03-0AB0', orderNumberBeforeV18: '6ES7 512-1SK01-0AB0' },
];
/** What each family is compiled for when the text names only the family (findTiaCpu's `family` match). */
export const TIA_REPRESENTATIVE_CPUS = {
    'S7-1200': CPU_1214C,
    'S7-1500': CPU_1511_1_PN,
    'ET 200SP': CPU_1512SP_1_PN,
};
/** The CPU compiled for when neither the project, the requirement nor the settings name one (E9). */
export const DEFAULT_TIA_CPU = CPU_1214C;
/**
 * The CPU one value names: a settings box, a card answer, a project's article
 * number. Besides what findTiaCpuInText reads, the whole value may be a bare
 * model number (1214, 1511), a bare family number (1200, 1500), an article
 * prefix (6ES7 21x, 6ES7 51x), or ET 200SP on its own.
 */
export function findTiaCpu(value) {
    return readCpu(value, true);
}
/**
 * The CPU a whole requirement names, with the words that name it for T1 to
 * quote (「需求写了型号就直接用」). This is the one reader of CPUs in text; T1
 * writes no other. A requirement's numbers are mostly lengths, speeds and
 * counts, so:
 *
 * - A model number (121x, 151x) counts only after CPU, S7-1200, S7-1500 or
 *   ET 200SP (「CPU 1516」「S7-1500 CPU 1516-3」), or written with a suffix only
 *   a model carries (1214C, 1214FC, 1512SP, 1511F, or -n before PN/DP as in
 *   1516-3 PN/DP). A -n alone is how ranges and codes read (1511-1520,
 *   2024-1511-1, 1516-3), so it is no suffix. A number followed by a unit or
 *   a count (mm, r/min, rpm, SPM, g, 件, 个…), or part of a decimal or a
 *   dimension, never counts without a suffix.
 * - An article needs three digits after 6ES7 (「6ES7 I/O」 is none), and one
 *   that names no CPU, a module say, is passed over.
 * - S7-1200, S7-1500 and 西门子 1200/1500 name a family. ET 200SP is also a
 *   distributed I/O system, commonly the remote I/O under an S7-1500 CPU, so
 *   it names the CPU family only beside "CPU" (or through a 151xSP model);
 *   elsewhere it names no CPU at all.
 * - `exact` and `family` as the table has it; the S7-1200 G2 models are not
 *   in the table.
 * - Named CPUs that disagree (a family and a model of another family, two
 *   families, two different models in the table or not, an F and a standard
 *   CPU) are a `conflict` of the first two that disagree. The same CPU named
 *   twice, by name and by article, is no conflict; nor is an article the
 *   table lacks, which tells only the family.
 * - A CPU RealPLC does not compile for, an S7-200 or S7-200 SMART, S7-300 or
 *   S7-400 named by article, family or CPU name, is `unsupported` when no CPU
 *   it does compile for is named (a retrofit's new CPU wins over the old).
 */
export function findTiaCpuInText(text) {
    return readCpu(text, false);
}
const ET_200SP_MODEL = /(?<!\d)(?<num>151\d)\s*SP(?:\s*(?<f>F)(?![A-Z]))?(?![A-Z])(?<dash>\s*-\s*\d(?!\d)(?:\s*PN)?)?/gi;
const S7_1500_MODEL = /(?<!\d)(?<num>151\d)(?!\d)(?:(?<gap>\s*)(?<variant>TF|HF|C|F|T|R|H)(?![A-Z]))?(?<dash>\s*-\s*\d(?!\d)(?<iface>\s*PN(?:\/DP)?|\s*DP)?)?/gi;
const S7_1200_MODEL = /(?<!\d)(?<num>121\d)(?!\d)(?:(?<gap>\s*)(?<variant>FC|C|F)(?![A-Z]))?(?<tail>\s*(?:AC|DC)\/DC\/(?:DC|RLY|Relay))?(?<g2>\s*G2(?![A-Z0-9]))?/gi;
const S7_FAMILY = /(?<![A-Z0-9])S7[\s-]*(?<series>1200|1500)(?!\d)(?:\s*G2(?![A-Z0-9]))?/gi;
const S7_1200_G2 = /(?<![A-Z0-9])S7[\s-]*1200\s*G2(?![A-Z0-9])/i;
const SIEMENS_SERIES = /(?:西门子|siemens)\s*(?<series>1200|1500)(?!\d)/gi;
const ET_200SP_FAMILY = /(?<![A-Z])ET[\s-]*200[\s-]*SP(?![A-Z])/gi;
/** S7-200 (and S7-200 SMART), S7-300 and S7-400 named in words: families RealPLC does not compile for. */
const UNSUPPORTED_FAMILY = /(?<![A-Z0-9])S7[\s-]*(?:200(?:\s*SMART)?|300|400)(?!\d)/gi;
const UNSUPPORTED_SIEMENS_SERIES = /(?:西门子|siemens)\s*(?:200|300|400)(?!\d)/gi;
/**
 * Their CPUs by name: CPU 221 to 226 (S7-200), CPU 312 to 319 (S7-300),
 * CPU 412 to 417 (S7-400), and the S7-200 SMART CPUs SRxx, STxx and CRxx.
 */
const UNSUPPORTED_CPU = /(?<![A-Z])CPU\s*(?:(?:22[1-6]|31[2-9]|41[2-7])(?!\d)[A-Z]{0,2}(?:\s*-\s*\d(?!\d))?(?:\s*(?:PN\/DP|PN|DP))?|(?:SR|ST|CRS|CR)\s*\d{2}(?!\d))/gi;
const ARTICLE = /(?<![A-Z0-9])6ES7[\s-]*\d{3}(?:[\s-]*[0-9A-Z]{5}(?:[\s-]*[0-9A-Z]{4})?)?/gi;
const ARTICLE_DIGITS = /6ES7[\s-]*\d{3}/i;
const ARTICLE_PREFIX = /^\s*(6ES7[\s-]*[25]1)[x*]?\s*$/i;
/** The words before a number that make it a model: CPU, a family name, or both. */
const MODEL_CONTEXT = /(?:(?:(?<![A-Z0-9])S7[\s-]*1[25]00(?!\d)(?:\s*G2)?|(?<![A-Z])ET[\s-]*200[\s-]*SP)\s*(?:CPU)?|CPU)\s*[:：]?\s*$/i;
/** What after a number makes it a quantity rather than a CPU: a decimal, a dimension, a unit, a count. */
const QUANTITY_AFTER = new RegExp('^(?:[.,]\\d'
    + '|\\s*[x×*]\\s*\\d'
    + '|\\s*(?:mm|cm|dm|km|m|µm|μm|um|nm|m/s|m/min|r/min|rpm|rps|spm|ppm|bpm|min|s|ms|us|μs|h|hz|khz|mhz'
    + '|kg|g|mg|t|n|kn|w|kw|v|kv|mv|a|ma|bar|mbar|pa|kpa|mpa|psi|l|ml|pcs|pc|deg|°c?|%)(?![a-z0-9])'
    + '|\\s*(?:℃|℉|件|个|台|套|只|片|根|块|条|张|瓶|箱|包|袋|桶|次|批|人|米|毫米|厘米|公里|千米|秒|毫秒|分钟|小时'
    + '|转|圈|度|公斤|千克|克|吨|牛|伏|安|瓦|千瓦|赫兹|升|毫升|帕|兆帕|元|年|月|日|号|路|点|位|步|层|格|轴|组|工位))', 'i');
const DECIMAL_BEFORE = /\d[.,]$/;
/**
 * Letters after 6ES7 51x-n that mark an ET 200SP CPU: D, and S for the F
 * version. 6ES7 514-2DN03-0AB0 is the CPU 1514SP-2 PN and 6ES7 510-1SK03-0AB0
 * the CPU 1510SP F-1 PN (their data sheets); every ET 200SP row agrees.
 */
const ET_200SP_ARTICLE_LETTERS = ['D', 'S'];
/**
 * The first generation digit of an S7-1200 CPU article, 6ES7 21x-1xx<digit>x:
 * 3 for the first firmware generations (6ES7 214-1AG31-0XB0, a CPU 1214C;
 * 6ES7 212-1AE31-0XB0, a CPU 1212C), 4 for the current one (the table's rows)
 * and 5 for the G2 CPUs (the S7-1200 G2 manual's ordering data, e.g.
 * 6ES7214-1AH50-0XB0 for its CPU 1214C DC/DC/DC). A 2 there, or earlier, is
 * an S7-200 sharing 6ES7 21x: 6ES7 214-1AD23-0XB0 is a CPU 224 (its data
 * sheet), which TIA cannot compile for.
 */
const S7_1200_GENERATION = /^[345]$/;
const compactOrderNumber = (text) => text.toUpperCase().replace(/[\s-]/g, '');
/** A named group of a match, or undefined when it took no part in it. */
const group = (found, name) => found.groups?.[name];
/** The model numbers in a text, read by the three families' grammars; nothing decides yet whether they count. */
function readModelTokens(text) {
    const tokens = [];
    const et200spStarts = new Set();
    for (const found of text.matchAll(ET_200SP_MODEL)) {
        const start = found.index ?? 0;
        et200spStarts.add(start);
        tokens.push({
            family: 'ET 200SP',
            key: `${group(found, 'num')}SP${group(found, 'f') ? 'F' : ''}`,
            suffixed: true,
            g2: false,
            start,
            end: start + found[0].length,
        });
    }
    for (const found of text.matchAll(S7_1500_MODEL)) {
        const start = found.index ?? 0;
        if (et200spStarts.has(start))
            continue;
        const variant = group(found, 'variant');
        const dash = group(found, 'dash');
        // A letter set apart by a space is a model's only before -n: 「1511 F-1 PN」, not 「1517 h」.
        const attached = variant !== undefined && group(found, 'gap') === '';
        const variantCounts = attached || (variant !== undefined && dash !== undefined);
        // A -n alone is also how ranges and codes read (1511-1520, 2024-1511-1, 1516-3), so it
        // makes a model only before PN/DP; after a context word the context does that.
        const suffixed = attached || group(found, 'iface') !== undefined;
        tokens.push({
            family: 'S7-1500',
            key: `${group(found, 'num')}${variantCounts ? variant.toUpperCase() : ''}`,
            suffixed,
            g2: false,
            start,
            end: start + (variantCounts || dash !== undefined ? found[0].length : 4),
        });
    }
    for (const found of text.matchAll(S7_1200_MODEL)) {
        const start = found.index ?? 0;
        const variant = group(found, 'variant');
        const tail = group(found, 'tail');
        const g2 = group(found, 'g2') !== undefined;
        // Siemens writes 「CPU 1214 FC」, so FC counts after a space; a lone C there may be Celsius.
        const variantCounts = variant !== undefined && (group(found, 'gap') === '' || variant.toUpperCase() === 'FC');
        const suffixed = variantCounts || tail !== undefined;
        tokens.push({
            family: 'S7-1200',
            key: `${group(found, 'num')}${variantCounts && /^F/i.test(variant) ? 'FC' : 'C'}`,
            suffixed,
            g2,
            start,
            end: start + (suffixed || g2 ? found[0].length : 4),
        });
    }
    return tokens;
}
/** The key of a table row's own model name, read by the same grammars, so a row always finds itself. */
const modelKeyOf = (model) => readModelTokens(model)[0]?.key;
/** A model token that counts as naming a CPU, as the table has it; null when it does not count. */
function acceptModel(text, token, single, g2Context) {
    const before = text.slice(0, token.start);
    const quantity = QUANTITY_AFTER.test(text.slice(token.start + 4)) || DECIMAL_BEFORE.test(before);
    if (!token.suffixed && quantity)
        return null;
    const context = MODEL_CONTEXT.exec(before);
    const wholeValue = single && before.trim() === '' && text.slice(token.end).trim() === '';
    if (!token.suffixed && !context && !wholeValue)
        return null;
    const named = token.family === 'S7-1200' && (token.g2 || g2Context)
        ? undefined
        : TIA_CPUS.find((cpu) => modelKeyOf(cpu.model) === token.key);
    const start = context ? context.index : token.start;
    return {
        cpu: named ?? TIA_REPRESENTATIVE_CPUS[token.family],
        match: named ? 'exact' : 'family',
        text: text.slice(start, token.end).trim(),
        start,
        specific: true,
        key: token.key,
    };
}
/**
 * What an article number names: a table row (current or pre-V18 article),
 * a family, a CPU RealPLC does not compile for, or nothing (a module or an
 * accessory). A partial 6ES7 21x names S7-1200 only as a whole value: in
 * running text the S7-200 CPUs share it.
 */
function readArticle(compact, single) {
    const named = TIA_CPUS.find((cpu) => [cpu.orderNumber, cpu.orderNumberBeforeV18]
        .some((orderNumber) => orderNumber !== undefined && compact.startsWith(compactOrderNumber(orderNumber))));
    if (named)
        return { cpu: named, match: 'exact' };
    switch (compact.slice(4, 6)) {
        case '51':
            return {
                cpu: TIA_REPRESENTATIVE_CPUS[ET_200SP_ARTICLE_LETTERS.includes(compact.charAt(8)) ? 'ET 200SP' : 'S7-1500'],
                match: 'family',
            };
        case '21':
            if (compact.length < 12)
                return single ? { cpu: CPU_1214C, match: 'family' } : null;
            return S7_1200_GENERATION.test(compact.charAt(10)) ? { cpu: CPU_1214C, match: 'family' } : 'unsupported';
        // S7-300 and S7-400 CPUs, e.g. 6ES7315-2AH14-0AB0, a CPU 315-2DP (its data sheet).
        case '31':
        case '41':
            return 'unsupported';
        // S7-200 SMART: 6ES7 288-1 is a CPU (6ES7288-1SR40-0AA0, the CPU SR40), the rest
        // its modules (6ES7288-2DE08-0AA0, a DI module; both from their data sheets).
        case '28':
            return compact.slice(4, 8) === '2881' ? 'unsupported' : null;
        default:
            return null;
    }
}
function readCpu(text, single) {
    const found = [];
    const unsupported = [];
    const family = (name, words, start) => ({ cpu: TIA_REPRESENTATIVE_CPUS[name], match: 'family', text: words.trim(), start, specific: false });
    for (const article of text.matchAll(ARTICLE)) {
        const read = readArticle(compactOrderNumber(article[0]), single);
        const words = article[0].trim();
        if (read === 'unsupported')
            unsupported.push({ text: words, start: article.index ?? 0 });
        else if (read) {
            const key = read.match === 'exact' ? modelKeyOf(read.cpu.model) : undefined;
            found.push({ ...read, text: words, start: article.index ?? 0, specific: true, key });
        }
    }
    const prefix = single ? ARTICLE_PREFIX.exec(text) : null;
    if (prefix?.[1]) {
        const read = readArticle(compactOrderNumber(prefix[1]), true);
        if (read && read !== 'unsupported')
            found.push({ ...read, text: text.trim(), start: 0, specific: false });
    }
    const g2Context = S7_1200_G2.test(text);
    for (const token of readModelTokens(text)) {
        const accepted = acceptModel(text, token, single, g2Context);
        if (accepted)
            found.push(accepted);
    }
    for (const name of text.matchAll(S7_FAMILY)) {
        found.push(family(group(name, 'series') === '1200' ? 'S7-1200' : 'S7-1500', name[0], name.index ?? 0));
    }
    for (const name of text.matchAll(SIEMENS_SERIES)) {
        const start = name.index ?? 0;
        if (QUANTITY_AFTER.test(text.slice(start + name[0].length)))
            continue;
        found.push(family(group(name, 'series') === '1200' ? 'S7-1200' : 'S7-1500', name[0], start));
    }
    for (const name of text.matchAll(ET_200SP_FAMILY)) {
        const start = name.index ?? 0;
        const before = text.slice(0, start);
        const after = text.slice(start + name[0].length);
        const besideCpu = /CPU\s*[:：]?\s*$/i.test(before) || /^\s*(?:的\s*)?CPU/i.test(after);
        const wholeValue = single && before.trim() === '' && after.trim() === '';
        if (besideCpu || wholeValue)
            found.push(family('ET 200SP', name[0], start));
    }
    const bare = single ? /^\s*(1200|1500)\s*$/.exec(text)?.[1] : undefined;
    if (bare)
        found.push(family(bare === '1200' ? 'S7-1200' : 'S7-1500', bare, text.indexOf(bare)));
    for (const pattern of [UNSUPPORTED_FAMILY, UNSUPPORTED_SIEMENS_SERIES, UNSUPPORTED_CPU]) {
        for (const name of text.matchAll(pattern)) {
            const start = name.index ?? 0;
            if (QUANTITY_AFTER.test(text.slice(start + name[0].length)))
                continue;
            unsupported.push({ text: name[0].trim(), start });
        }
    }
    return decide(found, unsupported);
}
function decide(found, unsupported) {
    const ordered = [...found].sort((a, b) => a.start - b.start);
    const [first] = ordered;
    if (first) {
        const otherFamily = ordered.find((mention) => mention.cpu.family !== first.cpu.family);
        if (otherFamily)
            return { match: 'conflict', mentions: [asMention(first), asMention(otherFamily)] };
        const models = ordered.filter((mention) => mention.key !== undefined);
        const [firstModel] = models;
        const otherModel = models.find((mention) => mention.key !== firstModel?.key);
        if (firstModel && otherModel)
            return { match: 'conflict', mentions: [asMention(firstModel), asMention(otherModel)] };
        const firstExact = ordered.find((mention) => mention.match === 'exact');
        return asMention(firstExact ?? ordered.find((mention) => mention.specific) ?? first);
    }
    const [firstUnsupported] = [...unsupported].sort((a, b) => a.start - b.start);
    return firstUnsupported ? { match: 'unsupported', text: firstUnsupported.text } : null;
}
const asMention = ({ cpu, match, text }) => ({ cpu, match, text });
const cpuName = (cpu) => `${cpu.family} ${cpu.model}`;
const oneLine = (text) => text.trim().replace(/\s+/g, ' ');
/** Shown only when the model's source is `default` (E9 ③). */
export const TIA_DEFAULT_CPU_NOTE = {
    zh: `CPU：${cpuName(DEFAULT_TIA_CPU)}（需求未指定型号，按默认型号编译；按 1200 编得过的程序一般在 1500 上也编得过，可修改）`,
    en: `CPU: ${cpuName(DEFAULT_TIA_CPU)} (the requirement names no model, so this default is used; `
        + 'a program that compiles for an S7-1200 usually compiles for an S7-1500 as well; you can change it)',
};
/**
 * For a model nothing in the table matches: the default is compiled for, and
 * the note quotes what the user wrote, on one line (E9 ③).
 */
export function tiaUnrecognisedCpuNote(userText) {
    const quoted = oneLine(userText);
    return {
        zh: `CPU：${cpuName(DEFAULT_TIA_CPU)}（认不出型号「${quoted}」，按默认型号编译，可修改）`,
        en: `CPU: ${cpuName(DEFAULT_TIA_CPU)} (the model "${quoted}" was not recognised, so this default is used; you can change it)`,
    };
}
/**
 * For a CPU RealPLC does not compile for (a lookup's `unsupported`): the
 * supported CPU its migration path leads to is compiled for instead, and the
 * note quotes what the user wrote, on one line. Not 「认不出」: the CPU was
 * recognised, just not supported.
 */
export function tiaUnsupportedCpuNote(userText, recommended) {
    const quoted = oneLine(userText);
    return {
        zh: `CPU：${cpuName(recommended)}（RealPLC 不为「${quoted}」生成和验证程序，按迁移路线改用这台编译，可修改）`,
        en: `CPU: ${cpuName(recommended)} (RealPLC does not generate or validate programs for "${quoted}", `
            + 'so the CPU on its migration path is used; you can change it)',
    };
}
/** How a family note opens, and how it says no model was given, by where the words came from. */
const FAMILY_NOTE_SOURCE = {
    requirement: { lead: { zh: '需求写的是', en: 'The requirement names' }, noModel: { zh: '没有写具体型号', en: ' but no model' } },
    settings: { lead: { zh: '设置里填的是', en: 'The settings give' }, noModel: { zh: '没有填具体型号', en: ' but no model' } },
    project: {
        lead: { zh: '所选工程的 CPU 是', en: 'The selected project\'s CPU is' },
        noModel: { zh: '没有具体型号', en: ', with no specific model' },
    },
};
/**
 * For a `family` match, which T1 shows: the words the lookup matched, where
 * they came from (the requirement, the settings, the selected project's
 * article), and what is compiled for instead. Words naming a model or an
 * article the table lacks read 「表里还没有它的订货号」; a family name or an
 * article prefix says no model was given, since saying the table lacks its
 * article would be wrong about a family. A selected project compiles on its
 * own CPU, so there the family only shapes the program.
 */
export function tiaFamilyCpuNote(mention, source) {
    const { cpu } = mention;
    const { lead, noModel } = FAMILY_NOTE_SOURCE[source];
    const quoted = oneLine(mention.text);
    const namesModel = readModelTokens(mention.text).length > 0 || ARTICLE_DIGITS.test(mention.text);
    const why = namesModel
        ? { zh: '表里还没有它的订货号', en: ', which the table has no article number for yet' }
        : noModel;
    if (source === 'project') {
        return {
            zh: `${lead.zh}「${quoted}」，${why.zh}，按 ${cpu.family} 系列写程序，在工程里的这台 CPU 上编译`,
            en: `${lead.en} "${quoted}"${why.en}, so the program is written for the ${cpu.family} family and compiled on that CPU in the project`,
        };
    }
    return namesModel
        ? {
            zh: `${lead.zh}「${quoted}」，${why.zh}，暂按同系列的 ${cpu.model} 编译，可修改`,
            en: `${lead.en} "${quoted}"${why.en}, so the same family's ${cpu.model} is used for now; you can change it`,
        }
        : {
            zh: `${lead.zh}「${quoted}」，${why.zh}，暂按 ${cpu.family} 系列的 ${cpu.model} 编译，可修改`,
            en: `${lead.en} "${quoted}"${why.en}, so the ${cpu.family} family's ${cpu.model} is used for now; you can change it`,
        };
}
/** E12: in the background unless the user chose the window. */
export const DEFAULT_TIA_WORK_MODE = 'background';
const TIA_WORK_MODES = { background: true, window: true };
/** Whether `value` is one of the work modes. */
export function isTiaWorkMode(value) {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(TIA_WORK_MODES, value);
}
//# sourceMappingURL=tiaProject.js.map